import { Link } from "react-router-dom";

// Two-panel format comparison (Raw Vial · Pre-filled Pen). Formerly a
// three-card section, Vial Kit was folded into Raw Vials in 2026-08.
// Convenience benefit is retained on the Raw Vial panel via the Recon
// Kit add-on line (actual toggle lives on the Raw Vial PDP).
//
// Layout: full-width edge-to-edge grid, solid backgrounds only (no
// dotted texture), PepDirect wordmark pinned bottom-right.
const CARDS = [
  {
    slug: "raw",
    label: "RAW VIAL",
    tagline: "Lowest cost per mg",
    bullets: [
      "Lyophilised, reconstitute yourself",
      "Manual dosing control",
      "Preferred by experienced researchers",
    ],
    // Raw Vial panel image, vial + BAC water + syringe hero shot.
    photo: "/product-photos/format/raw-vial-hero.webp",
    reconNote: true,
    ctaLabel: "SHOP RAW VIALS",
    ctaTo: "/raw-vials",
    testId: "format-choice-raw",
    active: false, // homepage/marketing instance has no "currently selected" context
  },
  {
    slug: "pen",
    label: "PRE-FILLED PEN",
    tagline: "Ready in ~20 seconds",
    bullets: [
      "Dial-a-dose, no reconstitution",
      "Mechanical accuracy ±1.5%",
      "Zero equipment needed to start",
    ],
    photo: "/product-photos/retatrutide-40mg-pen/pen_t.webp",
    reconNote: false,
    ctaLabel: "SHOP PEN KITS",
    ctaTo: "/pen-kits",
    testId: "format-choice-pen",
    active: false,
  },
];

export default function FormatChoice() {
  return (
    <section className="border-b border-line" data-testid="format-choice">
      <div className="container-pd py-16 lg:py-24">
        <div className="max-w-2xl">
          <p className="label-mono text-brandblue">↳ Two formats. One catalog.</p>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
            Pick the format that fits your workflow.
          </h2>
          <p className="mt-4 text-base text-subtle">
            Every compound ships across two formats. Same lot, same COA, same
            third-party HPLC results, the only difference is how you dose.
          </p>
        </div>

        {/* Edge-to-edge two-panel grid, full width of the container, no
            inner padding on the wrapping card so the two panels sit flush.
            Solid white background, no dotted/grid texture. PepDirect
            wordmark pinned bottom-right of the wrapper. */}
        <div className="relative mt-10 overflow-hidden rounded-2xl border border-line bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {CARDS.map((card) => (
              <div
                key={card.slug}
                className={`relative flex flex-col p-6 sm:p-8 ${
                  card.active
                    ? "bg-brandbluelight"
                    : "bg-white"
                } ${
                  // Divider between the two panels, vertical on desktop,
                  // horizontal on mobile stack.
                  card.slug === "pen" ? "border-t border-line md:border-l md:border-t-0" : ""
                }`}
                data-testid={card.testId}
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex w-fit items-center rounded-md border border-line bg-white/80 px-2 py-1 font-mono text-[10px] tracking-[0.16em] text-ink">
                    {card.label}
                  </span>
                  {card.active && (
                    <span
                      className="rounded-full bg-brandblue px-2.5 py-0.5 font-mono text-[9px] tracking-[0.12em] text-white"
                      data-testid={`${card.testId}-selected`}
                    >
                      SELECTED
                    </span>
                  )}
                </div>

                {/* Fixed-height image box (not aspect-ratio) so both
                    panels' images occupy the same vertical space and
                    the tagline/bullets below start at the exact same
                    Y on the two sides, even when the pen photo is
                    shorter than the raw vial photo. */}
                <div className="mt-5 flex h-56 w-full items-center justify-center sm:h-64 md:h-72">
                  <img
                    src={card.photo}
                    alt={`${card.label} format`}
                    className="max-h-full max-w-full object-contain"
                    draggable={false}
                    data-testid={`${card.testId}-photo`}
                  />
                </div>

                <p className="mt-5 font-heading text-lg font-bold text-ink">
                  {card.tagline}
                </p>
                <ul className="mt-3 space-y-2 text-sm text-ink">
                  {card.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brandblue" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                {card.reconNote && (
                  <p
                    className="mt-4 rounded-lg bg-white px-3 py-2 font-mono text-[10px] leading-snug tracking-[0.06em] text-brandbluedark ring-1 ring-brandblue/15"
                    data-testid={`${card.testId}-recon-note`}
                  >
                    + Recon Kit add-on available (BAC water + syringes), flip
                    it on at checkout for ready-to-draw convenience.
                  </p>
                )}

                {/* Wrapper with mt-auto pushes the CTA to the bottom of
                    the flex-column panel so both panels' buttons align
                    horizontally regardless of how much copy sits above. */}
                <div className="mt-auto pt-6">
                  <Link
                    to={card.ctaTo}
                    className="inline-flex w-fit items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-mono text-[11px] font-bold tracking-[0.12em] text-white transition-colors hover:bg-brandblue"
                    data-testid={`${card.testId}-cta`}
                  >
                    {card.ctaLabel} →
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <img
            src="/logos/pepdirect-full.webp"
            alt="PepDirect · Authorized Distributor"
            className="pointer-events-none absolute bottom-3 right-4 h-4 opacity-70"
            draggable={false}
            data-testid="format-choice-wordmark"
          />
        </div>
      </div>
    </section>
  );
}
